import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useSocial } from '../contexts/SocialContext';

/**
 * Per-page SEO helper. Drop <SEO title=... description=... /> at the top of any
 * page component to override the global defaults (set in <SocialMeta/>).
 *
 * Also injects JSON-LD structured data (Schema.org) for crawler comprehension.
 */
const upsertMeta = (selector, attrs) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    Object.entries(attrs).forEach(([k, v]) => {
      if (k !== 'content') el.setAttribute(k, v);
    });
    document.head.appendChild(el);
  }
  el.setAttribute('content', attrs.content || '');
};

const upsertLink = (rel, href) => {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

const upsertJsonLd = (id, data) => {
  let el = document.head.querySelector(`script[data-jsonld="${id}"]`);
  if (!el) {
    el = document.createElement('script');
    el.setAttribute('type', 'application/ld+json');
    el.setAttribute('data-jsonld', id);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
};

const ORG_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Löwenherz Invest AG',
  url: 'https://loewenherz.ag',
  logo: 'https://loewenherz.ag/logo.png',
  email: 'office@loewenherz.ag',
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Neuhausen am Rheinfall',
    addressCountry: 'CH',
  },
  description: 'Schweizer Immobilien-Investment-AG für Anleger, die langfristig in qualitativ hochwertige Liegenschaften investieren möchten.',
};

export const SEO = ({
  title,
  description,
  keywords,
  image,
  type = 'website',
  noindex = false,
  jsonLd,        // optional Schema.org payload
  breadcrumb,    // optional array of { name, url }
}) => {
  const { social } = useSocial();
  const location = useLocation();

  useEffect(() => {
    const finalTitle = title
      ? `${title} · Löwenherz Invest AG`
      : (social.ogTitle || 'Löwenherz Invest AG — Schweizer Immobilieninvestments');
    const finalDesc = description || social.ogDescription || '';
    const finalImg = image || social.ogImageUrl || '';
    const finalKeywords = keywords || 'Schweizer Immobilien, Immobilien Investment, Aktien Schweiz, Kapitalanlage, Sachwerte, Löwenherz';
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://loewenherz.ag';
    const url = `${origin}${location.pathname}${location.search}`;

    document.title = finalTitle;
    upsertMeta('meta[name="description"]', { name: 'description', content: finalDesc });
    upsertMeta('meta[name="keywords"]',    { name: 'keywords',    content: finalKeywords });
    upsertMeta('meta[name="robots"]',      { name: 'robots',      content: noindex ? 'noindex, nofollow' : 'index, follow' });

    upsertLink('canonical', url);

    upsertMeta('meta[property="og:title"]',       { property: 'og:title',       content: finalTitle });
    upsertMeta('meta[property="og:description"]', { property: 'og:description', content: finalDesc });
    upsertMeta('meta[property="og:type"]',        { property: 'og:type',        content: type });
    upsertMeta('meta[property="og:url"]',         { property: 'og:url',         content: url });
    upsertMeta('meta[property="og:site_name"]',   { property: 'og:site_name',   content: 'Löwenherz Invest AG' });
    upsertMeta('meta[property="og:locale"]',      { property: 'og:locale',      content: 'de_CH' });
    if (finalImg) upsertMeta('meta[property="og:image"]', { property: 'og:image', content: finalImg });

    upsertMeta('meta[name="twitter:card"]',        { name: 'twitter:card',        content: finalImg ? 'summary_large_image' : 'summary' });
    upsertMeta('meta[name="twitter:title"]',       { name: 'twitter:title',       content: finalTitle });
    upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: finalDesc });
    if (finalImg) upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: finalImg });

    // Always-on Organization schema
    upsertJsonLd('org', ORG_JSONLD);

    if (jsonLd) {
      upsertJsonLd('page', jsonLd);
    }

    if (breadcrumb && breadcrumb.length) {
      upsertJsonLd('breadcrumb', {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumb.map((b, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: b.name,
          item: b.url,
        })),
      });
    }
  }, [title, description, image, keywords, type, noindex, jsonLd, breadcrumb, social, location.pathname, location.search]);

  return null;
};
